<template>
  <transition name="view" appear>
    <div class="item-select" v-if="currentView">
      <div
        v-if="currentView.dropdown && currentView.dropdown.show"
        class="dropdown"
      >
        <RexSearchSelect
          width="14.875rem"
          :data="buckets"
          :current="currentBucket"
          :searchPlaceholder="$t('dbm.header.bucketDropdownheader')"
          :heading="$t('dbm.header.bucketDropdownheader')"
          @onChoose="onBucketChange"
          :objectValueIndex="1"
          :boldActive="true"
          id="bucket-select"
        />
      </div>
      <div
        class="card-body"
        v-if="currentView.cards && currentView.cards.template"
      >
        <div
          v-for="(card, cardIndex) in currentView.cards.template.cardName"
          :key="cardIndex"
          :style="{
            width: currentView.cards.template.width,
          }"
          class="cards"
        >
          <InfoStats
            class="info-border"
            :textLabel="card"
            :textValue="textValue[cardIndex]"
            :fontSize="fontSize"
            textValHeight="3rem"
            lineHeight="1.2rem"
            :textValColor="
              textValue[cardIndex] === 'Active'
                ? 'good'
                : textValue[cardIndex] === 'Inactive'
                  ? 'poor'
                  : 'fair'
            "
          />
        </div>
      </div>
      <div
        class="wrapper"
        v-if="currentView.graphs && currentView.graphs.template"
      >
        <div
          class="chart-element"
          v-for="(chartConfig, index) in getGraphCharts[0]"
          :key="index + 'linw' + chartConfig.metricName"
          :style="{ width: chartConfig.width }"
        >
          <GraphBuilder
            :chartConfig="chartConfig"
            :index="index"
            :layoutIndex="0"
          />
        </div>
        <RexProgressCard
          v-if="currentView.progressCard && currentView.progressCard.show"
          class="chart-element"
          :headerName="diskUtility"
          :headerLegends="headerLegends"
          :items="diskItems"
          :loading="loading"
          :progressTailLabelColor="progressTailLabelColor"
          :colorInfo="colorInfo"
        />
        <div
          class="chart-element"
          v-for="(chartConfig, index) in getGraphCharts[1]"
          :key="index + 'linw' + chartConfig.metricName"
          :style="{ width: chartConfig.width }"
        >
          <GraphBuilder
            :chartConfig="chartConfig"
            :index="index"
            :layoutIndex="1"
          />
        </div>
      </div>
      <div
        v-if="currentView.query && currentView.query.show"
        class="query-dropdown"
      >
        <RexSearchSelect
          width="15rem"
          :data="dropdownValues"
          :current="currentQueryTimeThreshold"
          :isSearch="false"
          :heading="$t('queryDbTimeThresholdDropdown')"
          @onChoose="onQueryTimeThresholdChange"
          :objectValueIndex="1"
          id="query-threshold-select"
        />
        <DatabaseQueryDetailsPdf
          fileName="couchbase-query-details"
          :headers="headers"
          :queryTimeThreshold="currentQueryTimeThreshold"
          :disabled="isQueryDetailsEmpty"
          :sortCriteria="getSortCriteria"
        />
      </div>
      <div v-if="currentView.table && currentView.table.show">
        <RexTableCompV2
          :headers="headers"
          :items="queryItemsApp"
          height="37em"
          :striped="true"
          @fetchNextPageData="
            () => {
              if (allowInfiniteScroll) {
                currentPage++;
                getQueryPerformanceTableData();
              }
            }
          "
          :enableInfiniteScroll="true"
          :enableScrollFooter="dispScrollFooter"
          :loading="loading"
        >
          <template
            v-for="header in headers"
            #[`body-${header.key}`]="{ item }"
            :key="`${header.key}-${getRandomUniqueId()}`"
          >
            <div v-if="header.key === 'timestamp'">
              {{
                $filters.dateformat(
                  parseInt(item[header.key]),
                  timezoneoffset,
                  'DD.MM.YY [at] hh:mm A',
                )
              }}
            </div>
            <div v-else-if="header.key === 'value'">
              {{
                item[header.key]
                  ? formatValue(item[header.key])
                  : item[header.key]
              }}
            </div>
            <div
              v-else-if="header.key === 'name'"
              :key="`${header.key}-${getRandomUniqueId()}`"
              class="query-details"
            >
              <span class="query">
                {{ item[header.key] }}
              </span>
              <span
                class="copy-icon-box"
                v-tooltip:right="$t('copy')"
                @click="handleCopy(item[header.key])"
              >
                <BaseIcon name="copyNew" iconColor="#f59600" />
              </span>
            </div>
          </template>
          <template
            v-for="header in headers"
            #[`header-${header.key}`]
            :key="header.key"
          >
            <div>
              <RexTableSortField
                v-if="header.sortable"
                :name="header.name"
                @toggleSort="() => toggleSorting(header.key)"
                :isAsc="isAsc"
                :isActiveField="sortingField === header.key"
              />
              <div v-else>
                {{ header.name }}
              </div>
            </div>
          </template>
          <template v-if="dispInfiniteFooterMsg" #infinite-footer>
            <div class="infi-footer">
              <div v-if="isScrollErr">
                {{ $t('dbm.errorWhileFetchingData') }}
                <button class="refresh-btn">
                  <BaseIcon
                    name="refresh"
                    @click="
                      resetTableData();
                      getQueryPerformanceTableData();
                    "
                    height="0.8em"
                    width="0.8em"
                    class="refresh-icon"
                  />
                </button>
              </div>
              <div v-else>
                {{ $t('dbm.availableDataLoadedCompletely') }}
              </div>
            </div>
          </template>
        </RexTableCompV2>
      </div>
    </div>
  </transition>
</template>

<script>
import GraphBuilder from '@/components/Common/GraphBuilder.vue';
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';
import RexSearchSelect from '@/rex-components/RexSearchSelect.vue';
import InfoStats from '@/rex-components/rex-info-stats.vue';
import RexProgressCard from '@/rex-components/RexProgressCard.vue';
import RexTableComp from '@/rex-components/rex-TableComp.vue';
import * as types from '@/components/Database/database.types';
import * as constants from '@/utils/templates/databaseTemplate/database.constants';
import RexTableSortField from '@/rex-components/rex-TableSortField.vue';
import {
  QueryThresholdValues,
  queryPerformanceTableHeadersCouchbase,
} from '@/utils/templates/databaseTemplate/couchbase.query.table.config';
import RexTableCompV2 from '@/rex-components/rex-TableCompV2.vue';
import { getRandomUniqueId } from '@/utils/helper-function';
import copy from '@/utils/copy';
import { notificationSucess } from '@/utils/notification';
import { convertMilliseconds } from '@/utils/helper-function';
import DatabaseQueryDetailsPdf from '@/components/Database/DatabaseContent/DatabaseQueryDetailsPdf.vue';
import databaseCharts from '@/utils/templates/database.charts.template';

export default {
  name: 'Couchbase',
  data() {
    return {
      showTotal: false,
      showProgressTop: true,
      progressHeaderColor: true,
      colorInfo: false,
      dropdownValues: QueryThresholdValues,
      currentQueryTimeThreshold: {},
      NodeLevelQueryDetail: this.$t('dbm.query.nodeLevelQueryDetail'),
      bucketDorpdownValues: [],
      queryItemsApp: [],
      diskUtility: this.$t('dbm.header.diskUtility'),
      fontSize: '16px',
      diskData: [],
      nodeDiskData: [],
      isAsc: false,
      pageSize: 20,
      loading: true,
      currentPage: 1,
      total: 0,
      isScrollErr: false,
      progressTailLabelColor: false,
      headerLegends: [
        {
          name: this.$t('dbm.progressLegends.progressLegendUsed'),
          class: 'primary',
        },
        {
          name: this.$t('dbm.progressLegends.progressLegendAvailable'),
          class: 'secondary',
        },
      ],
      sortingField: types.timeStamp,
    };
  },
  components: {
    GraphBuilder,
    RexSearchSelect,
    InfoStats,
    RexProgressCard,
    RexTableComp,
    RexTableCompV2,
    RexTableSortField,
    DatabaseQueryDetailsPdf,
  },
  computed: {
    ...mapState('globalState/app', ['chartsTemplate']),
    ...mapState('databaseState/databaseCommonState', [
      'currentDatabaseType',
      'currentDatabaseService',
      'databaseQuery',
    ]),
    ...mapState('databaseState/detail', ['currentSubType']),
    ...mapState('databaseState/detail', [
      'currentDatabaseInstance',
      'currentSubType',
      'databaseInstances',
      'buckets',
      'currentBucket',
    ]),
    ...mapGetters('globalState/settings', ['getDurationTime', 'intervalTime']),
    ...mapState('globalState/settings', ['timezoneoffset']),
    ...mapGetters('databaseState/databaseCommonState', ['getCurrentDatabase']),

    currentView() {
      if (
        this.currentDatabaseType &&
        this.currentSubType &&
        this.currentDatabaseType.key == types.DatabaseTypes.cb
      )
        return (
          this.chartsTemplate?.[this.currentDatabaseType?.key]?.[
            this.currentSubType?.key
          ] || {}
        );
      else return {};
    },
    getGraphCharts() {
      return this.currentDatabaseType?.key === types.DatabaseTypes.cb
        ? databaseCharts()[this.currentDatabaseType?.key]?.[
            this.currentSubType?.key
          ]?.graphs?.template
        : {};
    },
    dispScrollFooter() {
      return this.queryItemsApp?.length >= 15;
    },
    allowInfiniteScroll() {
      return this.queryItemsApp?.length < this.total;
    },
    dispInfiniteFooterMsg() {
      return (
        (!this.allowInfiniteScroll || this.isScrollErr) && this.dispScrollFooter
      );
    },

    textValue() {
      if (this.currentDatabaseInstance && this.currentDatabaseInstance.key) {
        let status;
        let uptimeValue;
        const attributes = this.currentDatabaseInstance?.attributes;
        const CardDatavalues = this.currentView.cards.template.cardName.map(
          (name) => {
            let CardDatavalue = this.$t('noData');
            switch (name) {
              case types.couchbaseCardNames.CLUSTER_NAME:
                CardDatavalue = attributes?.find(
                  (attr) => attr.name === types.couchbaseCardValues.CB_NAME,
                )?.value;
                break;
              case types.couchbaseCardNames.COUCHBASE_VERSION:
                CardDatavalue = attributes?.find(
                  (attr) => attr.name === types.couchbaseCardValues.CB_VERSION,
                )?.value;
                break;
              case types.couchbaseCardNames.NO_OF_NODES:
                CardDatavalue = attributes?.find(
                  (attr) =>
                    attr.name === types.couchbaseCardValues.CB_NO_OF_NODES,
                )?.value;
                break;
              case types.couchbaseCardNames.STATUS:
                status = this.currentDatabaseInstance?.isActive;
                CardDatavalue =
                  status === true
                    ? types.cardValues.ACTIVE
                    : types.cardValues.INACTIVE;
                break;
              case types.couchbaseCardNames.NO_OF_BUCKETS:
                CardDatavalue = attributes?.find(
                  (attr) =>
                    attr.name === types.couchbaseCardValues.CB_NO_OF_BUCKETS,
                )?.value;
                break;
              case types.couchbaseCardNames.NODE_NAME:
                CardDatavalue = attributes?.find(
                  (attr) =>
                    attr.name === types.couchbaseCardValues.CB_NODE_NAME,
                )?.value;
                break;
              case types.couchbaseCardNames.NODE_VERSION:
                CardDatavalue = attributes?.find(
                  (attr) =>
                    attr.name === types.couchbaseCardValues.CB_NODE_VERSION,
                )?.value;
                break;
              case types.couchbaseCardNames.NODE_UPTIME:
                uptimeValue = attributes?.find(
                  (attr) =>
                    attr.name === types.couchbaseCardValues.CB_NODE_UPTIME,
                )?.value;
                if (uptimeValue) {
                  CardDatavalue = this.convertUptime(uptimeValue);
                }
                break;
              case types.couchbaseCardNames.NODE_STATUS:
                CardDatavalue = attributes?.find(
                  (attr) =>
                    attr.name === types.couchbaseCardValues.CB_NODE_STATUS,
                )?.value;
                break;
              case types.couchbaseCardNames.NODE_OS:
                CardDatavalue = attributes?.find(
                  (attr) => attr.name === types.couchbaseCardValues.CB_NODE_OS,
                )?.value;
                break;
            }
            return CardDatavalue || this.$t('noData');
          },
        );

        return CardDatavalues;
      }
      return this.currentView.cards.template.cardName.map(() =>
        this.$t('noData'),
      );
    },
    diskItems() {
      if (this.currentSubType.key === constants.SUB_TYPES.CB_CLUSTER) {
        return this.diskData;
      } else if (this.currentSubType.key === constants.SUB_TYPES.CB_NODE) {
        return this.nodeDiskData;
      } else return [];
    },
    isQueryDetailsEmpty() {
      return this.queryItemsApp?.length === 0;
    },
    headers() {
      return queryPerformanceTableHeadersCouchbase();
    },
    getSortCriteria() {
      return [
        {
          field: types.queryTableSortFieldMapping[this.sortingField],
          order: this.isAsc ? constants.ASC : constants.DES,
        },
      ];
    },
  },
  async created() {
    this.currentQueryTimeThreshold = this.dropdownValues[2];
    await this.fetchNodeData();
    this.resetTableData();
    this.getQueryPerformanceTableData();
    this.getDiskData();
  },
  methods: {
    ...mapMutations('databaseState/detail', [
      'setCurrentDatabaseInstance',
      'setDatabaseInstances',
      'setBuckets',
      'setCurrentBucket',
    ]),
    ...mapActions('databaseState/detail', [
      'getDatabaseInstances',
      'getBucketDetails',
      'getInstancesByMetaPropertyWithTotal',
    ]),
    ...mapActions('databaseState/databaseCommonState', [
      'GET_ALL_QUERY_RECORDS',
      'GET_DB_DISK_DATA',
    ]),
    getRandomUniqueId,
    formatTableResp(list) {
      return list.map((item) => {
        return { _id: `${getRandomUniqueId()}`, ...item };
      });
    },
    formatValue(value) {
      const numericValue = Number(value);
      if (Number.isInteger(numericValue)) {
        return numericValue;
      } else {
        return parseFloat(numericValue).toFixed(2);
      }
    },
    handleCopy(content) {
      copy(JSON.stringify(content));
      notificationSucess(this.$t('copied'));
    },
    async fetchNodeData() {
      let databaseInstance = {
        duration: this.getDurationTime,
        serviceId: this.currentDatabaseService.key,
      };
      if (this.currentDatabaseService && this.currentDatabaseService.key) {
        if (this.currentSubType.key === constants.SUB_TYPES.CB_CLUSTER) {
          databaseInstance.properties = [
            {
              key: 'metaProperty3',
              value: constants.CLUSTER,
            },
          ];
        } else {
          databaseInstance.properties = [
            {
              key: 'metaProperty3',
              value: constants.NODE,
            },
          ];
        }
        await this.getInstancesByMetaPropertyWithTotal(databaseInstance);
      }
    },
    async fetchBucketData() {
      if (
        this.currentDatabaseInstance &&
        this.currentDatabaseInstance.key &&
        this.currentSubType.key === constants.SUB_TYPES.CB_BUCKET
      ) {
        let bucket = {
          duration: this.getDurationTime,
          serviceId: this.currentDatabaseService.key,
        };
        bucket.properties = [
          {
            key: constants.METAPROPERTY1,
            value: this.currentDatabaseInstance.label,
          },
          {
            key: constants.METAPROPERTY3,
            value: constants.BUCKET,
          },
        ];
        await this.getBucketDetails(bucket);
      }
    },
    onBucketChange(option) {
      this.setCurrentBucket(option);
    },
    resetTableData() {
      this.queryItemsApp = [];
      this.loading = true;
      this.total = 0;
      this.currentPage = 1;
    },
    toggleSorting(sortingField) {
      this.isAsc = !this.isAsc;
      this.sortingField = sortingField;
      this.resetTableData();
      this.getQueryPerformanceTableData();
    },
    onQueryTimeThresholdChange(data) {
      this.currentQueryTimeThreshold = data;
      this.resetTableData();
      this.getQueryPerformanceTableData();
    },
    async getQueryPerformanceTableData() {
      try {
        this.loading = true;
        if (
          this.currentDatabaseInstance.key &&
          this.currentSubType.key &&
          this.currentSubType.key === constants.SUB_TYPES.CB_QUERIES
        ) {
          const params = {
            condition: {
              name: types.metricTypes.DBM_QUERIES,
              parentService: this.currentDatabaseInstance.key,
              scope: 'ServiceInstance',
              normal: false,
              searchKeyword: '*',
              queryTimeThreshold: this.currentQueryTimeThreshold.key,
            },
            duration: this.getDurationTime,
            pagination: {
              pageNum: this.currentPage,
              pageSize: this.pageSize,
              needTotal: true,
            },
            sortCriteria: this.getSortCriteria,
          };
          const queryData = await this.GET_ALL_QUERY_RECORDS(params);
          this.total = queryData.total;
          this.queryItemsApp.push(
            ...this.formatTableResp(queryData?.records ?? []),
          );
        } else {
          this.queryItemsApp = [];
          this.total = 0;
          this.isScrollErr = true;
        }
        this.loading = false;
      } catch (error) {
        this.queryItemsApp = [];
        this.isScrollErr = true;
        this.loading = false;
      }
    },

    async getDiskData() {
      if (this.currentSubType.key === constants.SUB_TYPES.CB_CLUSTER) {
        await this.getClusterDiskData();
      }
      if (this.currentSubType.key === constants.SUB_TYPES.CB_NODE) {
        await this.getNodeDiskData();
      }
    },

    async getClusterDiskData() {
      try {
        if (this.currentDatabaseService.key) {
          this.diskData = await this.GET_DB_DISK_DATA({
            scope: 'ServiceInstance',
            serviceId: this.currentDatabaseService.key,
            duration: this.getDurationTime,
            serviceInstanceName:
              this.currentDatabaseService.key +
              '_' +
              btoa(this.currentDatabaseService.label),
            usedMetricName:
              types.metricCouchbaseTypes.METER_COUCHBASE_DISK_SIZE_USED,
            totalMetricName:
              types.metricCouchbaseTypes.METER_COUCHBASE_DISK_SIZE_TOTAL,
          });
          this.diskData.forEach((item) => {
            item.used.textValue = this.$t('dbm.usedMemory');
            item.total.textValue = this.$t('dbm.totalMemory');
            item.available.textValue = this.$t('dbm.availableMemory');
          });
          this.colorInfo = true;
          this.loading = false;
        }
      } catch (error) {
        this.diskData = [];
        this.colorInfo = false;
      } finally {
        this.loading = false;
      }
    },

    async getNodeDiskData() {
      try {
        if (this.currentDatabaseInstance && this.currentDatabaseInstance.key) {
          this.nodeDiskData = await this.GET_DB_DISK_DATA({
            scope: 'Endpoint',
            serviceId: this.currentDatabaseService.key,
            duration: this.getDurationTime,
            endpointName: this.currentDatabaseInstance.key,
            usedMetricName:
              types.metricCouchbaseTypes.METER_COUCHBASE_NODE_DISK_SIZE_USED,
            totalMetricName:
              types.metricCouchbaseTypes.METER_COUCHBASE_NODE_DISK_SIZE_TOTAL,
          });
          this.nodeDiskData.forEach((item) => {
            item.used.textValue = this.$t('dbm.usedMemory');
            item.total.textValue = this.$t('dbm.totalMemory');
            item.available.textValue = this.$t('dbm.availableMemory');
          });
          this.colorInfo = true;
          this.loading = false;
        }
      } catch (error) {
        this.nodeDiskData = [];
        this.colorInfo = false;
      } finally {
        this.loading = false;
      }
    },
    convertUptime(value) {
      const dateObject = convertMilliseconds(value * 1000);
      if (dateObject.days > 0) {
        return this.$t('dbm.dateobject-days-days-dateobject-hours-hours', [
          dateObject.days,
          dateObject.hours,
        ]);
      } else if (dateObject.hours > 0) {
        return this.$t(
          'dbm.dateobject-hours-hours-dateobject-minutes-minutes',
          [dateObject.hours, dateObject.minutes],
        );
      } else if (dateObject.minutes > 0) {
        return this.$t(
          'dbm.dateobject-minutes-minutes-dateobject-seconds-seconds',
          [dateObject.minutes, dateObject.seconds],
        );
      } else if (dateObject.seconds > 0) {
        return this.$t(
          'dbm.dateobject-seconds-seconds-dateobject-milliseconds-milliseconds',
          [dateObject.seconds, dateObject.milliseconds],
        );
      }
      return `${value} milliseconds`;
    },
  },

  watch: {
    'currentDatabaseService.key': function () {
      this.fetchNodeData()
        .then(() => {
          this.getDiskData();
        })
        .catch((error) => {
          console.error(error);
        });
    },
    'currentSubType.key': function () {
      this.isAsc = false;
      this.fetchNodeData()
        .then(() => {
          this.getDiskData();
        })
        .catch((error) => {
          console.error(error);
        });
    },
    getDurationTime(newValue) {
      if (newValue) {
        this.fetchNodeData()
          .then(() => {
            this.getDiskData();
            this.resetTableData();
            this.getQueryPerformanceTableData();
          })
          .catch((error) => {
            console.error(error);
          });
      }
    },
    currentDatabaseInstance() {
      this.setBuckets([]);
      this.setCurrentBucket({});
      this.fetchBucketData();
      this.getDiskData();
      this.resetTableData();
      this.getQueryPerformanceTableData();
      this.fetchBucketData();
    },
  },
};
</script>

<style lang="scss" scoped>
.wrapper {
  width: 100%;
  @include flex-center-justify(space-between);
  flex-wrap: wrap;
  margin-top: 1.438em;
}
.item-select {
  margin-top: 50px !important;
}
.chart-element {
  width: 49%;
  margin-bottom: 1.5em;
  margin-top: 0.125em;
  height: 23.75em;
}
.info-border {
  border-radius: 0.5rem;
}

.card-body {
  background-color: #1e2021;
  border-radius: 0.5rem;
  display: flex;
  padding: 1rem;
}

.cards {
  padding: 0.5rem;
}
:deep(.body-container) {
  display: flex;
}

.dropdown {
  margin-left: 0.1375rem;
  text-align: left;
}
.pagination {
  width: 96%;
}
:deep(.body-td-content) {
  word-break: break-all;
}
.table {
  color: $color-white;
}
.table-box {
  @include flex-column;

  margin-right: 1em;
}
.copy-icon-box {
  cursor: pointer;
  @include svg-fill-pointer($color-brand-accent);
  padding-left: 1rem;
  align-self: center;
}
.query-dropdown {
  @include flex-center-justify(space-between);
  margin: 0.5rem;
  padding-bottom: 1rem;
}
.infi-footer {
  @include flex-center;
  color: $font-tertiary;
  padding: 1.25rem 0rem;
}
.refresh-btn {
  border: none;
  background-color: inherit;
}
.refresh-icon {
  @include svg-fill-pointer($color-brand-accent);
}
.query-details {
  display: flex;
  width: 100%;
}
.query {
  width: 88%;
}
:deep(.progress-tail-content-identifier) {
  color: $font-tertiary;
}

:deep(.progress-tail-content-identifier-value) {
  color: $font-primary;
}

:deep(.total-color) {
  color: $font-tertiary;
}
:deep(.total-value-color) {
  margin-left: 0.75rem;
}
:deep(.textVal) {
  word-break: break-all;
}
</style>
